import {
  ACTION,
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  Button,
  ButtonVariants,
  Drawer,
  FormControlSizes,
  HStack,
  IconName,
  Panel,
  PanelHeader,
  Stack,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useDrawer,
  useDynamicCallback,
  usePersistedTabs,
  usePortal,
  useTabs,
  type CustomerTransaction,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useState } from 'react';
import type { CustomerTransactionsTabsProps } from './types';

import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { useDetailsDrawer } from '../../../components/DetailsDrawer/useDetailsDrawer';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import {
  CUSTOMER_BALANCE_TRANSACTION_DRAWER_WIDTH,
  CustomerBalanceTransactionDrawer,
} from '../CustomerBalances/CustomerBalanceTransactionDrawer';
import { LegacyBalanceTransactionDrawer } from '../CustomerBalances/LegacyBalanceTransactionDrawer';
import { CustomerTransactionsBlotter } from './CustomerTransactionsBlotter';
import { DEFAULT_CUSTOMER_TRANSACTION_TABS } from './defaults';

const CUSTOMER_TRANSACTIONS_BLOTTER_PREFIX = 'dealer/customer-transactions';

const tabLabeler = tabLabelerEnumerated('Transfers');

const TABS_EXCLUDED_IDS = compact(DEFAULT_CUSTOMER_TRANSACTION_TABS.map(tab => tab.id));
function getBlotterID(tabID: string | undefined) {
  return `${CUSTOMER_TRANSACTIONS_BLOTTER_PREFIX}/${tabID}`;
}

type CustomerTransactionsProps = {
  showHeader?: boolean;
};

export const CustomerTransactions = (props: CustomerTransactionsProps) => {
  const { showHeader = true } = props;
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);
  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => setInitialFiltersOpen(true), []);
  const { enableNewCustomerBalanceTransactionDrawer } = useFeatureFlag();

  const persistedTabs = usePersistedTabs<CustomerTransactionsTabsProps>(CUSTOMER_TRANSACTIONS_BLOTTER_PREFIX, {
    defaultInitialItems: DEFAULT_CUSTOMER_TRANSACTION_TABS,
    excludedIDs: TABS_EXCLUDED_IDS,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useDynamicCallback(() => {
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  });

  const handleUpdateTab = useDynamicCallback((updatedTab: CustomerTransactionsTabsProps) => {
    tabs.updateItem(updatedTab);
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const { openDetailsDrawer, closeDetailsDrawer, isOpen: isDrawerOpen } = useDetailsDrawer();

  const [lastSelectedRowID, setLastSelectedRowID] = useState<string | undefined>();
  const handleDoubleClickRow = useDynamicCallback(({ data }: RowDoubleClickedEvent<CustomerTransaction>) => {
    if (!data) {
      return;
    }
    const id = data.TransactionID;
    // If clicking the same row, close the drawer
    if (lastSelectedRowID === id && isDrawerOpen) {
      setLastSelectedRowID(undefined);
      closeDetailsDrawer();
      return;
    }

    // If clicking a different row, open the drawer
    openDetailsDrawer({ entity: data });
    setLastSelectedRowID(id);
  });

  const balanceTransactionDrawer = useDrawer({
    position: 'relative',
    width: CUSTOMER_BALANCE_TRANSACTION_DRAWER_WIDTH,
  });

  const { isAuthorized } = useRoleAuth();

  return (
    <HStack h="100%" w="100%" gap="spacing2" overflow="hidden">
      <Panel>
        {showHeader && (
          <PanelHeader>
            <h2>Customer Transfers</h2>
            <Button
              startIcon={IconName.ArrowLeftRight}
              onClick={() => balanceTransactionDrawer.open()}
              variant={ButtonVariants.Positive}
              disabled={!isAuthorized(ACTION.EDIT_CUSTOMER_BALANCE)}
              size={FormControlSizes.Small}
              data-testid="customer-transfers-new-balance-transaction-button"
            >
              New Balance Transaction
            </Button>
          </PanelHeader>
        )}

        <Stack overflow="hidden" flex="1" background="backgroundContent">
          <Tabs h="100%" {...tabs} size={TabSize.Large}>
            <TabList
              flex="0 0 auto"
              isBordered
              rightItems={
                <HStack gap="spacing12">
                  <Box ref={filtersContainerRef} />
                </HStack>
              }
            >
              {tabs.items.map((tab, index) => (
                <Tab key={index} {...tab} />
              ))}
            </TabList>
            <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
              {tabs.items.map(tab => (
                <AccordionGroup key={tab.id}>
                  <CustomerTransactionsBlotter
                    tab={tab}
                    blotterID={`${CUSTOMER_TRANSACTIONS_BLOTTER_PREFIX}/${tab.id}`}
                    initialFiltersOpen={initialFiltersOpen}
                    onCloneTab={handleCloneTab}
                    onUpdateTab={handleUpdateTab}
                    onRowDoubleClicked={handleDoubleClickRow}
                  />
                </AccordionGroup>
              ))}
            </TabPanels>
          </Tabs>
        </Stack>
      </Panel>
      <Drawer {...balanceTransactionDrawer} isOpen={balanceTransactionDrawer.isOpen}>
        {balanceTransactionDrawer.isOpen &&
          (enableNewCustomerBalanceTransactionDrawer ? (
            <CustomerBalanceTransactionDrawer onClose={() => balanceTransactionDrawer.close()} />
          ) : (
            <LegacyBalanceTransactionDrawer
              isOpen={balanceTransactionDrawer.isOpen}
              onClose={() => balanceTransactionDrawer.close()}
            />
          ))}
      </Drawer>
    </HStack>
  );
};
